import { useState, useEffect, useCallback } from 'react'
import { Box, VStack } from '@chakra-ui/react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { BrandBar } from '../../components/BrandBar'
import { TitleHeader } from '../../components/TitleHeader'
import { PurchaseSummary } from '../components/PurchaseSummary'
import { PaymentMethodDisplay } from '../components/PaymentMethodDisplay'
import { PurchaseButton } from '../components/PurchaseButton'
import { BackToSelectPaymentLink } from '../components/BackToSelectPaymentLink'
import { useCreditCardPurchase } from '../hooks/useCreditCardPurchase'
import { useSaveCard } from '../hooks/useSaveCard'
import { useErrorToast } from '../hooks/useErrorToast'
import { getSaveCardErrorMessage, getPurchaseErrorMessage } from '../services/purchaseConfirmErrors'
import { useLogout } from '../../hooks/useLogout'

export const PurchaseConfirm = ({ coinPlans }) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const { showError } = useErrorToast()
  const { logout, isLoading: isLogoutLoading } = useLogout()

  const productId = searchParams.get('product_id')
  const coinPlan = coinPlans?.find((plan) => plan.product_id === productId)
  const gmoToken = location.state?.gmoToken // 新規カード登録時のトークン
  const newCardLast4 = location.state?.cardLast4 // 新規カード登録時のカード下4桁
  const cardInfo = location.state?.cardInfo // 既存カード情報（SelectPaymentMethodから受け取る）
  const isUpdate = location.state?.isUpdate || false // カード更新フラグ

  // カスタムフック
  const { purchaseWithCredit } = useCreditCardPurchase()
  const { saveCard } = useSaveCard()

  // 購入処理中フラグ（API呼び出し開始〜画面遷移完了まで）
  const [isPurchaseProcessing, setIsPurchaseProcessing] = useState(false)

  // hasUsedCard判定: cardInfoがある場合は既存カード、gmoTokenがある場合は新規カード
  const hasUsedCard = !!cardInfo && !gmoToken
  // 表示用のカード下4桁: 既存カードの場合はcardInfo.last4、新規カードの場合はnewCardLast4
  const displayCardLast4 = hasUsedCard ? cardInfo?.last4 : newCardLast4

  // product_idが不正な場合はリダイレクト
  useEffect(() => {
    if (coinPlans && coinPlans.length > 0 && (!productId || !coinPlan)) {
      showError({
        title: 'コインが選択されていません',
        errorMessage: '',
        duration: 3000,
      })
      navigate('/')
    }
  }, [productId, coinPlan, coinPlans, navigate, showError])

  // カード情報チェック: 既存カードも新規トークンもない場合はリダイレクト
  useEffect(() => {
    // productIdが有効な場合のみカード情報をチェック
    if (!productId || !coinPlan) return

    if (!cardInfo && !gmoToken) {
      showError({
        title: 'カード情報が取得できませんでした',
        errorMessage: '',
        duration: 3000,
      })
      navigate(`/select_payment_method?product_id=${productId}`)
    }
  }, [cardInfo, gmoToken, productId, coinPlan, navigate, showError])

  // 購入ボタン押下時の処理
  const handlePurchase = useCallback(async () => {
    setIsPurchaseProcessing(true)

    // 新規カードの場合はsave_card APIを呼び出し
    if (!hasUsedCard && gmoToken) {
      try {
        await saveCard(gmoToken, isUpdate)
      } catch (err) {
        setIsPurchaseProcessing(false)
        const errorData = err.data || {}
        const errorMessage = getSaveCardErrorMessage(errorData.error, errorData.card_info_error_type)
        showError({
          title: 'エラー',
          errorMessage,
        })
        return
      }
    }

    // クレジットカード決済
    try {
      const result = await purchaseWithCredit(productId)

      if (result.redirect_url) {
        // ブラウザバック対策: replaceを使用して現在のページを履歴に残さずに遷移
        // これにより3Dセキュア画面からブラウザバックしてもPurchaseConfirmには戻れない
        window.location.replace(result.redirect_url)
      } else {
        // 成功時は購入完了画面へ遷移
        navigate('/complete', {
          state: {
            purchasedCoinCount: result.coin_count,
            totalCoinCount: result.total_coin_count,
            price: result.price,
          },
        })
      }
    } catch (err) {
      setIsPurchaseProcessing(false)
      const errorData = err.data || {}
      const errorMessage = getPurchaseErrorMessage(errorData.error, errorData.card_info_error_type)
      showError({
        title: 'エラー',
        errorMessage,
      })
    }
  }, [hasUsedCard, gmoToken, isUpdate, saveCard, showError, purchaseWithCredit, productId, navigate])

  // 決済方法変更リンク押下時
  const handleChangePaymentMethod = useCallback(() => {
    if (isPurchaseProcessing) return
    navigate(`/select_payment_method?product_id=${productId}`)
  }, [isPurchaseProcessing, navigate, productId])

  // 他のカードを使うリンク押下時
  const handleChangeCard = useCallback(() => {
    if (isPurchaseProcessing) return
    navigate(`/new_card?product_id=${productId}`, {
      state: { isUpdate: true }, // 常にカード更新
    })
  }, [isPurchaseProcessing, navigate, productId])

  // 戻るリンク押下時
  const handleBack = useCallback(() => {
    if (isPurchaseProcessing) return

    // 新規カード決済時はnew_card画面へ、既存カード決済時はselect_payment_method画面へ
    if (hasUsedCard) {
      navigate(`/select_payment_method?product_id=${productId}`)
    } else {
      navigate(`/new_card?product_id=${productId}`)
    }
  }, [isPurchaseProcessing, hasUsedCard, navigate, productId])

  // データ読み込み中またはコインプランが見つからない場合は何も表示しない
  if (!coinPlan) {
    return null
  }

  return (
    <Box minH="100vh" bg="background.default" display="flex" justifyContent="center">
      <Box w="100%" maxW="500px">
        <VStack spacing={0} align="stretch">
          <BrandBar onLogout={logout} isLogoutLoading={isLogoutLoading} onLogoClick={() => navigate('/')} />
          <TitleHeader title="購入内容確認" />

          <Box py="20px" px="16px">
            <VStack spacing={6} align="stretch">
              {/* 購入内容サマリー */}
              <PurchaseSummary coinCount={coinPlan.coin} price={coinPlan.web_price} />

              {/* 決済手段 */}
              <PaymentMethodDisplay
                cardLast4={displayCardLast4}
                hasUsedCard={hasUsedCard}
                cardNumberMasked={cardInfo?.cardNo}
                onChangePaymentMethod={handleChangePaymentMethod}
                onChangeCard={handleChangeCard}
              />

              {/* 購入ボタン */}
              <PurchaseButton onClick={handlePurchase} isLoading={isPurchaseProcessing} />

              {/* 戻るリンク */}
              <BackToSelectPaymentLink onClick={handleBack} />
            </VStack>
          </Box>
        </VStack>
      </Box>
    </Box>
  )
}
